import { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { cn } from '@most/ui';

/**
 * Custom voice-message player for the support chat.
 *
 * Replaces the native `<audio controls>` bar (which renders differently per browser and
 * ignores our palette) with a compact, theme-matched player: play/pause, a scrubbable
 * waveform-style progress bar, a running time, and a tap-to-cycle playback speed.
 *
 * `mine` flips the colour scheme to sit on the dark (buyer) or light (support) bubble.
 */
export function AudioMessage({
  src,
  mine,
  className,
}: {
  src: string;
  mine: boolean;
  className?: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [rate, setRate] = useState(1);
  const [ready, setReady] = useState(false);

  // Keep React state in sync with the media element's own events.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onLoaded = () => {
      // Some encoders report Infinity until the stream is fully buffered.
      if (Number.isFinite(el.duration)) setDuration(el.duration);
      setReady(true);
    };
    const onTime = () => setCurrent(el.currentTime);
    const onEnd = () => {
      setPlaying(false);
      setCurrent(0);
      el.currentTime = 0;
    };
    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('durationchange', onLoaded);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('play', () => setPlaying(true));
    el.addEventListener('pause', () => setPlaying(false));
    el.addEventListener('ended', onEnd);
    return () => {
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('durationchange', onLoaded);
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  }, []);

  const seek = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const el = audioRef.current;
      if (!el || !duration) return;
      const t = (Number(e.target.value) / 100) * duration;
      el.currentTime = t;
      setCurrent(t);
    },
    [duration],
  );

  const cycleRate = useCallback(() => {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  }, [rate]);

  const pct = duration ? Math.min(100, (current / duration) * 100) : 0;
  // While playing show elapsed; otherwise show total (mirrors WhatsApp/Telegram behaviour).
  const shownTime = playing || current > 0 ? current : duration;

  const accent = mine ? 'text-white' : 'text-slate-900';
  const track = mine ? 'bg-white/25' : 'bg-slate-300';
  const fill = mine ? 'bg-white' : 'bg-slate-800';

  return (
    <div className={cn('flex w-56 max-w-full items-center gap-2.5', className)}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={src} preload="metadata" />

      <button
        type="button"
        onClick={toggle}
        disabled={!ready}
        aria-label={playing ? 'Pause' : 'Play'}
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition disabled:opacity-50',
          mine ? 'bg-white/15 hover:bg-white/25' : 'bg-slate-900/5 hover:bg-slate-900/10',
          accent,
        )}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
      </button>

      <div className="min-w-0 flex-1">
        {/* Scrubber: a thin track with a fill and a native range on top for a11y/drag. */}
        <div className="relative h-1.5">
          <div className={cn('absolute inset-0 rounded-full', track)} />
          <div
            className={cn('absolute inset-y-0 left-0 rounded-full', fill)}
            style={{ width: `${pct}%` }}
          />
          <input
            type="range"
            min={0}
            max={100}
            step={0.1}
            value={pct}
            onChange={seek}
            disabled={!ready}
            aria-label="Seek"
            className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent
              [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:appearance-none
              [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-current
              [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-current"
            style={{ color: mine ? '#fff' : '#1e293b' }}
          />
        </div>

        <div className={cn('mt-1 flex items-center justify-between text-[10px] tabular-nums', mine ? 'text-white/70' : 'text-slate-500')}>
          <span>{fmtTime(shownTime)}</span>
          <button
            type="button"
            onClick={cycleRate}
            aria-label="Playback speed"
            className={cn(
              'rounded px-1 font-semibold transition',
              mine ? 'hover:bg-white/15' : 'hover:bg-slate-900/10',
            )}
          >
            {rate}×
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
